import React from 'react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Button, Box, IconButton, Typography } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import FolderIcon from '@mui/icons-material/Folder';
import BookIcon from '@mui/icons-material/Book';
import HelpIcon from '@mui/icons-material/Help';
import ScienceIcon from '@mui/icons-material/Science';
import PaymentIcon from '@mui/icons-material/Payment';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import TranslateIcon from '@mui/icons-material/Translate';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Collapse from '@mui/material/Collapse';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from './Sidebar.module.css';
import api from '../api';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { useAuth } from '../contexts/AuthContext';

// Removed: knowledgeBaseSubjects (now dynamic)


interface Subject {
  _id?: string;
  id?: string;
  name: string;
  slug?: string;
}

const Sidebar: React.FC<{ open: boolean; onClose: () => void; variant?: 'permanent' | 'temporary' | 'persistent' }> = ({ open, onClose, variant = 'permanent' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = React.useState(false);
  const [openKbDropdown, setOpenKbDropdown] = React.useState(false);
  const [subjects, setSubjects] = React.useState<Subject[]>([]);
  const [researchAreas, setResearchAreas] = React.useState<any[]>([]);
  const [waecSections, setWaecSections] = React.useState<Subject[]>([]);
  const [jambSections, setJambSections] = React.useState<Subject[]>([]);
  const { canInstall, installApp, isInstalled } = usePWAInstall();
  const { user, isAuthenticated } = useAuth();
  
  // Fallback to localStorage in case auth context isn't updated yet
  let effectiveUser = user;
  if (!effectiveUser) {
    try {
      const raw = localStorage.getItem('user');
      if (raw) effectiveUser = JSON.parse(raw);
    } catch (e) {
      // ignore parse errors
    }
  }
  
  const isHighSchoolUser = effectiveUser?.educationLevel === 'high-school';
  const hasSchoolWorkAccess = Boolean(
    effectiveUser?.educationLevel === 'high-school' &&
    (effectiveUser?.school || effectiveUser?.schoolName || effectiveUser?.schoolEmail)
  );
  const shouldForceHSLogin = isHighSchoolUser && !isAuthenticated;

  const navigateWithHSFallback = (path: string) => {
    if (shouldForceHSLogin) {
      navigate('/high-school/login');
    } else {
      navigate(path);
    }
    if (variant !== 'permanent') {
      onClose();
    }
  };

  const handleNavClick = (path?: string) => {
    if (path) {
      navigateWithHSFallback(path);
    } else if (variant !== 'permanent') {
      onClose();
    }
  };

  const [openWaecDropdown, setOpenWaecDropdown] = React.useState(false);
  const [openJambDropdown, setOpenJambDropdown] = React.useState(false);

  // Fetch research areas from backend so the public sidebar is driven by admin-managed content
  React.useEffect(() => {
    let mounted = true;
    const fetchResearchAreas = async () => {
      try {
        const res = await api.get('/research-areas');
        // API may return { data: [...] } or an array directly
        let data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        if (!Array.isArray(data)) data = [];
        // Data may be array of strings or objects. Normalize to objects with title and optional order.
        const areas = data
          .map((d: any) => {
            if (typeof d === 'string') return { title: d, order: undefined };
            return { ...d, title: d.title || d.name || '', order: typeof d.order === 'number' ? d.order : undefined };
          })
          .filter((a: any) => a.title);
        // Sort strategy:
        // - Items with an explicit numeric `order` come first (ascending)
        // - Items without `order` are sorted alphabetically by title (case-insensitive)
        areas.sort((a: any, b: any) => {
          const ao = typeof a.order === 'number' ? a.order : null;
          const bo = typeof b.order === 'number' ? b.order : null;
          if (ao !== null && bo !== null) {
            if (ao !== bo) return ao - bo;
          } else if (ao !== null) {
            return -1;
          } else if (bo !== null) {
            return 1;
          }
          return String(a.title).localeCompare(String(b.title), undefined, { sensitivity: 'base' });
        });
        if (mounted) setResearchAreas(areas);
      } catch (err) {
        if (mounted) setResearchAreas([]);
      }
    };
    fetchResearchAreas();
    return () => { mounted = false; };
  }, []);

  React.useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const res = await api.get('/knowledge-base/subjects', { withCredentials: true });
        const data = res.data;
        setSubjects(Array.isArray(data) ? data : (data?.data || []));
      } catch {
        setSubjects([]);
      }
    };
    fetchSubjects();
  }, []);

  React.useEffect(() => {
    const fetchWaecSections = async () => {
      try {
        const res = await api.get('/waec-sections', { withCredentials: true });
        const data = res.data;
        const sections = Array.isArray(data) ? data : (data?.data || []);
        setWaecSections(sections);
      } catch {
        setWaecSections([]);
      }
    };
    fetchWaecSections();
  }, []);

  React.useEffect(() => {
    const fetchJambSections = async () => {
      try {
        const res = await api.get('/jamb-sections', { withCredentials: true });
        const data = res.data;
        const sections = Array.isArray(data) ? data : (data?.data || []);
        setJambSections(sections);
      } catch {
        setJambSections([]);
      }
    };
    fetchJambSections();
  }, []);

  const handleKbDropdownClick = () => {
    setOpenKbDropdown((prev) => !prev);
  };

  const handleKbSubjectClick = (subject: Subject) => {
    if (subject.slug) {
      navigateWithHSFallback(`/knowledge-base/${subject.slug}`);
      setOpenKbDropdown(false);
    }
  };

  const handleWaecSectionClick = (section: Subject) => {
    if (section.slug) {
      navigateWithHSFallback(`/waec/${section.slug}`);
      setOpenWaecDropdown(false);
    }
  };

  const handleJambSectionClick = (section: Subject) => {
    if (section.slug) {
      navigateWithHSFallback(`/jamb/${section.slug}`);
      setOpenJambDropdown(false);
    }
  };

  const handleDropdownClick = () => {
    setOpenDropdown((prev) => !prev);
  };

  const handleResearchAreaClick = (area: string) => {
    const keyword = area.toLowerCase().replace(/\s+/g, '-');
    navigateWithHSFallback(`/projects?category=${encodeURIComponent(keyword)}`);
  };

  const handleWaecDropdownClick = () => {
    setOpenWaecDropdown((prev) => !prev);
  };

  const handleJambDropdownClick = () => {
    setOpenJambDropdown((prev) => !prev);
  };

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      PaperProps={{ className: styles.sidebarPaper, elevation: variant === 'temporary' ? 16 : undefined }}
      ModalProps={{
        keepMounted: true, // Better mobile performance
        // Keep the temporary drawer above the fixed mobile bottom navigation.
        sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
      }}
      sx={{
        // For temporary drawer on mobile - let MUI handle positioning and sizing
        ...(variant === 'temporary' && {
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            backgroundColor: (theme) => theme.palette.background.paper,
            color: (theme) => theme.palette.text.primary,
            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            height: '100dvh',
            overflow: 'hidden',
          },
        }),
        // For persistent drawer on desktop
        ...(variant === 'persistent' && {
          '& .MuiDrawer-paper': {
            position: 'fixed',
            top: 64,
            left: 0,
            height: 'calc(100vh - 64px)',
            zIndex: 1200,
          },
        }),
      }}
    >
      <React.Fragment>
        {/* Mobile drawer header with close button */}
        {variant !== 'permanent' && (
          <Box className={styles.sidebarHeader}>
            <Box className={styles.brandTitleBox}>
              <Box component="img" src={`${process.env.PUBLIC_URL}/logo_with_bottom_text.svg`} alt="A3Tech Education" sx={{ width: 72, height: 48, objectFit: 'contain' }} />
            </Box>
            <IconButton 
              onClick={onClose} 
              className={styles.closeDrawerBtn}
              aria-label="Close navigation sidebar"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}

        <Box className={styles.sidebarTop}>
          {/* Show Download App button only when installable and not already in app mode */}
          {canInstall && !isInstalled && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              className={styles.downloadAppBtn}
              onClick={() => installApp()}
              fullWidth
            >
              Download App
            </Button>
          )}
        </Box>
        <List className={styles.sidebarNav} sx={{ px: 1.5, py: 1.5 }}>
          {isHighSchoolUser ? (
            // High-school navigation aligned to the actual routes used in the app.
            <>
              <Typography variant="overline" sx={{ px: 2, pb: 1, display: 'block', color: 'text.secondary', letterSpacing: '0.12em' }}>
                Overview
              </Typography>
              <ListItem
                button
                onClick={() => navigateWithHSFallback('/')}
                selected={location.pathname === '/'}
                className={styles.sidebarItem}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><HomeIcon /></ListItemIcon>
                <ListItemText primary="Home" className={styles.sidebarListItemText} />
              </ListItem>

              <Typography variant="overline" sx={{ px: 2, py: 1.5, display: 'block', color: 'text.secondary', letterSpacing: '0.12em' }}>
                Study
              </Typography>
              <ListItem
                button
                onClick={handleWaecDropdownClick}
                selected={location.pathname.startsWith('/waec') || openWaecDropdown}
                className={styles.sidebarItem}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><SchoolIcon /></ListItemIcon>
                <ListItemText primary="WAEC" className={styles.sidebarListItemText} />
                <ExpandMoreIcon
                  className={styles.chevronIcon}
                  style={{
                    transform: openWaecDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </ListItem>
              <Collapse in={openWaecDropdown} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {waecSections.length > 0 ? (
                    waecSections.map((section) => (
                      <ListItem
                        button
                        key={section.slug || section._id || section.id}
                        className={styles.sidebarSubItem}
                        onClick={() => handleWaecSectionClick(section)}
                      >
                        <ListItemText
                          primary={section.name}
                          className={styles.sidebarSubItemText}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem className={styles.sidebarSubItem}>
                      <ListItemText
                        primary="No subjects yet"
                        className={styles.sidebarSubItemText}
                        primaryTypographyProps={{ sx: { opacity: 0.6 } }}
                      />
                    </ListItem>
                  )}
                </List>
              </Collapse>

              <ListItem
                button
                onClick={() => navigateWithHSFallback('/mock-test/waec')}
                selected={location.pathname.startsWith('/mock-test/waec')}
                className={styles.sidebarItem}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><ScienceIcon /></ListItemIcon>
                <ListItemText primary="WAEC Mock Test" className={styles.sidebarListItemText} />
              </ListItem>

              <ListItem
                button
                onClick={handleJambDropdownClick}
                selected={location.pathname.startsWith('/jamb') || openJambDropdown}
                className={styles.sidebarItem}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><SchoolIcon /></ListItemIcon>
                <ListItemText primary="JAMB" className={styles.sidebarListItemText} />
                <ExpandMoreIcon
                  className={styles.chevronIcon}
                  style={{
                    transform: openJambDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </ListItem>
              <Collapse in={openJambDropdown} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {jambSections.length > 0 ? (
                    jambSections.map((section) => (
                      <ListItem
                        button
                        key={section.slug || section._id || section.id}
                        className={styles.sidebarSubItem}
                        onClick={() => handleJambSectionClick(section)}
                      >
                        <ListItemText
                          primary={section.name}
                          className={styles.sidebarSubItemText}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem className={styles.sidebarSubItem}>
                      <ListItemText
                        primary="No subjects yet"
                        className={styles.sidebarSubItemText}
                        primaryTypographyProps={{ sx: { opacity: 0.6 } }}
                      />
                    </ListItem>
                  )}
                </List>
              </Collapse>

              <ListItem
                button
                onClick={() => navigateWithHSFallback('/mock-test/jamb')}
                selected={location.pathname.startsWith('/mock-test/jamb')}
                className={styles.sidebarItem}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><ScienceIcon /></ListItemIcon>
                <ListItemText primary="JAMB Mock Test" className={styles.sidebarListItemText} />
              </ListItem>

              <ListItem
                button
                onClick={() => navigateWithHSFallback('/discussion-rooms')}
                selected={location.pathname.startsWith('/discussion-rooms')}
                className={styles.sidebarItem}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><ForumOutlinedIcon /></ListItemIcon>
                <ListItemText primary="Discussion Room" className={styles.sidebarListItemText} />
              </ListItem>

              {hasSchoolWorkAccess && (
                <>
                  <ListItem
                    button
                    onClick={() => navigateWithHSFallback('/school-work')}
                    selected={location.pathname === '/school-work'}
                    className={styles.sidebarItem}
                  >
                    <ListItemIcon className={styles.sidebarItemIcon}><BookIcon /></ListItemIcon>
                    <ListItemText primary="School Work" className={styles.sidebarListItemText} />
                  </ListItem>
                  <ListItem
                    button
                    onClick={() => navigateWithHSFallback('/school-work/materials')}
                    selected={location.pathname === '/school-work/materials'}
                    className={styles.sidebarItem}
                  >
                    <ListItemIcon className={styles.sidebarItemIcon}><BookIcon /></ListItemIcon>
                    <ListItemText primary="Materials" className={styles.sidebarListItemText} />
                  </ListItem>
                  <ListItem
                    button
                    onClick={() => navigateWithHSFallback('/school-work/grades')}
                    selected={location.pathname === '/school-work/grades'}
                    className={styles.sidebarItem}
                  >
                    <ListItemIcon className={styles.sidebarItemIcon}><HistoryEduIcon /></ListItemIcon>
                    <ListItemText primary="Grades" className={styles.sidebarListItemText} />
                  </ListItem>
                  <ListItem
                    button
                    onClick={() => navigateWithHSFallback('/school-work/term-history')}
                    selected={location.pathname === '/school-work/term-history'}
                    className={styles.sidebarItem}
                  >
                    <ListItemIcon className={styles.sidebarItemIcon}><HistoryEduIcon /></ListItemIcon>
                    <ListItemText primary="Term History" className={styles.sidebarListItemText} />
                  </ListItem>
                </>
              )}

              <Typography variant="overline" sx={{ px: 2, py: 1.5, display: 'block', color: 'text.secondary', letterSpacing: '0.12em' }}>
                Tools
              </Typography>
              <ListItem
                button
                component={Link}
                to="/labs"
                selected={location.pathname.startsWith('/labs')}
                className={styles.sidebarItem}
                onClick={() => handleNavClick()}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><ScienceIcon /></ListItemIcon>
                <ListItemText primary="Labs" className={styles.sidebarListItemText} />
              </ListItem>

              <ListItem
                button
                component={Link}
                to="/language-hub"
                selected={location.pathname.startsWith('/language-hub')}
                className={styles.sidebarItem}
                onClick={() => handleNavClick()}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><TranslateIcon /></ListItemIcon>
                <ListItemText primary="Language Hub" className={styles.sidebarListItemText} />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/high-school/contact"
                selected={location.pathname.startsWith('/high-school/contact')}
                className={styles.sidebarItem}
                onClick={() => handleNavClick()}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><HelpIcon /></ListItemIcon>
                <ListItemText primary="Contact Support" className={styles.sidebarListItemText} />
              </ListItem>

              <ListItem
                button
                component={Link}
                to="/chat"
                selected={location.pathname.startsWith('/chat')}
                className={styles.sidebarItem}
                onClick={() => handleNavClick()}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><HelpIcon /></ListItemIcon>
                <ListItemText primary="AI Tutor" className={styles.sidebarListItemText} />
              </ListItem>

            </>
          ) : (
            // Default full sidebar for other users
            <>
              <ListItem
                button
                onClick={handleKbDropdownClick}
                selected={location.pathname.startsWith('/knowledge-base') || openKbDropdown}
                className={styles.sidebarItem}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><MenuBookIcon /></ListItemIcon>
                <ListItemText primary="Knowledge Base" className={styles.sidebarListItemText} />
                <ExpandMoreIcon
                  className={styles.chevronIcon}
                  style={{
                    transform: openKbDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </ListItem>
              <Collapse in={openKbDropdown} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {subjects.map((subject) => (
                    <ListItem
                      button
                      key={subject.slug || subject._id || subject.id}
                      className={styles.sidebarSubItem}
                      onClick={() => handleKbSubjectClick(subject)}
                    >
                      <ListItemText
                        primary={subject.name}
                        className={styles.sidebarSubItemText}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
              <ListItem
                button
                component={Link}
                to="/"
                selected={location.pathname === '/'}
                className={styles.sidebarItem}
                onClick={() => handleNavClick()}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><HomeIcon /></ListItemIcon>
                <ListItemText primary="Home" className={styles.sidebarListItemText} />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/language-hub"
                selected={location.pathname.startsWith('/language-hub')}
                className={styles.sidebarItem}
                onClick={() => handleNavClick()}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><TranslateIcon /></ListItemIcon>
                <ListItemText primary="Language Hub" className={styles.sidebarListItemText} />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/projects"
                selected={location.pathname.startsWith('/projects')}
                className={styles.sidebarItem}
                onClick={() => handleNavClick()}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><FolderIcon /></ListItemIcon>
                <ListItemText primary="Projects" className={styles.sidebarListItemText} />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/blog"
                selected={location.pathname.startsWith('/blog')}
                className={styles.sidebarItem}
                onClick={() => handleNavClick()}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><BookIcon /></ListItemIcon>
                <ListItemText primary="Blog" className={styles.sidebarListItemText} />
              </ListItem>
              {(effectiveUser?.parentName || location.pathname.startsWith('/parent')) && (
                <ListItem
                  button
                  component={Link}
                  to="/parent/payments"
                  selected={location.pathname.startsWith('/parent/payments')}
                  className={styles.sidebarItem}
                  onClick={() => handleNavClick()}
                >
                  <ListItemIcon className={styles.sidebarItemIcon}><PaymentIcon /></ListItemIcon>
                  <ListItemText primary="School Fees" className={styles.sidebarListItemText} />
                </ListItem>
              )}
              <ListItem
                button
                onClick={handleDropdownClick}
                selected={openDropdown}
                className={styles.sidebarItem}
              >
                <ListItemIcon className={styles.sidebarItemIcon}><ScienceIcon /></ListItemIcon>
                <ListItemText primary="Research Areas" className={styles.sidebarListItemText} />
                <ExpandMoreIcon
                  className={styles.chevronIcon}
                  style={{
                    transform: openDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </ListItem>
              <Collapse in={openDropdown} timeout="auto" unmountOnExit>
                <List component="div" disablePadding aria-label="Research areas (ordered by site admin)">
                  <span className={styles.visuallyHidden}>Research areas ordered by site admin</span>
                  {researchAreas.map((area: any) => (
                    <ListItem
                      button
                      key={area._id || area.title}
                      className={styles.sidebarSubItem}
                      onClick={() => handleResearchAreaClick(area.title)}
                    >
                      <ListItemText
                        primary={area.title}
                        className={styles.sidebarSubItemText}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}
        </List>
        <Box className={styles.sidebarFooter}>
          <List disablePadding>
            <ListItem
              button
              component={Link}
              to="/faq"
              selected={location.pathname.startsWith('/faq')}
              className={styles.sidebarItem}
              onClick={() => handleNavClick()}
            >
              <ListItemIcon className={styles.sidebarItemIcon}><HelpIcon /></ListItemIcon>
              <ListItemText primary="FAQS" className={styles.sidebarListItemText} />
            </ListItem>
          </List>
        </Box>
      </React.Fragment>
    </Drawer>
  );
};

export default Sidebar;
